import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { AuthProvider as OidcAuthProvider, useAuth } from 'react-oidc-context';
import { useLocation } from 'react-router-dom';
import { recordSessionAuditAction } from '../service/form';
import { authDebug, getAuthDebugError } from '../utils/auth-debug';
import { getSelectedFormIdFromSession } from '../utils/form-id';
import { getTenantFromPath } from '../utils/tenant-utils';
import { MembershipProvider } from './MembershipContext';
import { buildSignOutUrl, oidcConfig } from './oidcConfig';

export const SIGN_OUT_TENANT_KEY = 'signOutTenant';
const SESSION_ROLE_KEY_PREFIX = 'activeSessionRole:';
const SELECTED_FORM_ID_KEY_PREFIX = 'selectedFormId:';

export type AuthUser = {
  email: string;
  sub: string;
};

export interface AuthContextProps {
  isAuthenticated: boolean;
  /** True while the OIDC client is restoring a session or exchanging the sign-in code. */
  isLoading: boolean;
  user?: AuthUser;
  fetchJWT(options?: { redirectOnFail?: boolean }): Promise<{
    accessToken?: string;
    idToken?: string;
  }>;
  /** Sends the browser through the GRID hosted UI to COLA; `returnTo` comes back in the OIDC state. */
  signIn(returnTo: string): Promise<void>;
  /** Ends the local, Cognito and COLA sessions, then lands on the tenant page. */
  signOut(): Promise<void>;
}

export const AuthContext = createContext<AuthContextProps | null>(null);

const clearSessionKeys = () => {
  try {
    Object.keys(window.sessionStorage)
      .filter(
        (key) =>
          key.startsWith(SESSION_ROLE_KEY_PREFIX) || key.startsWith(SELECTED_FORM_ID_KEY_PREFIX),
      )
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch (_err) {}
};

const AuthBridge = ({ children }: PropsWithChildren) => {
  const auth = useAuth();
  const location = useLocation();

  const signIn = useCallback(
    async (returnTo: string) => {
      authDebug('oidc.signin.start', { returnTo });
      await auth.signinRedirect({ state: { returnTo } });
    },
    [auth],
  );

  const fetchJWT = useCallback(
    async (
      options: { redirectOnFail?: boolean } = {},
    ): Promise<{ accessToken?: string; idToken?: string }> => {
      const { redirectOnFail = true } = options;
      let user = auth.user ?? null;
      if (user?.expired) {
        try {
          user = await auth.signinSilent();
        } catch (error) {
          authDebug('oidc.silentRenew.error', { error: getAuthDebugError(error) });
          user = null;
        }
      }
      if (user?.id_token) {
        return { idToken: user.id_token, accessToken: user.access_token };
      }
      if (redirectOnFail) {
        void signIn(`${location.pathname}${location.search || ''}`);
      }
      return {};
    },
    [auth, location.pathname, location.search, signIn],
  );

  const signOut = useCallback(async () => {
    const tenant = getTenantFromPath(window.location.pathname);
    const idToken = auth.user && !auth.user.expired ? auth.user.id_token : undefined;
    if (idToken) {
      try {
        const selectedFormId = getSelectedFormIdFromSession(tenant);
        await recordSessionAuditAction(
          idToken,
          'logout',
          selectedFormId ? { form_id: selectedFormId } : undefined,
        );
      } catch (_err) {
        // Session audit must never block sign-out.
      }
    }
    try {
      window.sessionStorage.setItem(SIGN_OUT_TENANT_KEY, tenant || '');
    } catch (_err) {}
    try {
      await auth.removeUser();
    } catch (_err) {}
    clearSessionKeys();
    window.location.assign(buildSignOutUrl());
  }, [auth]);

  const user = useMemo<AuthUser | undefined>(() => {
    const profile = auth.user?.profile;
    const sub = typeof profile?.sub === 'string' ? profile.sub.trim() : '';
    if (!auth.user || !sub) return undefined;
    const email = typeof profile?.email === 'string' ? profile.email.trim().toLowerCase() : '';
    return { sub, email };
  }, [auth.user]);

  const value = useMemo<AuthContextProps>(
    () => ({
      isAuthenticated: Boolean(auth.isAuthenticated),
      isLoading: Boolean(auth.isLoading),
      user,
      fetchJWT,
      signIn,
      signOut,
    }),
    [auth.isAuthenticated, auth.isLoading, fetchJWT, signIn, signOut, user],
  );

  return (
    <AuthContext.Provider value={value}>
      <MembershipProvider isAuthenticated={value.isAuthenticated} fetchJWT={fetchJWT}>
        {children}
      </MembershipProvider>
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }: PropsWithChildren) => (
  <OidcAuthProvider {...oidcConfig}>
    <AuthBridge>{children}</AuthBridge>
  </OidcAuthProvider>
);

export const useAuthContext = (): AuthContextProps => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw Error('No AuthProvider found.');
  }
  return authContext;
};
